import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Typography,
  ThemeProvider,
  createTheme,
  Autocomplete,
  CircularProgress,
  Box,
  Grid,
  Divider,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import apiClient from "@lift-admin/utils/apiClient";
import { Toastify } from 'lift-ui';
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { actionLog } from "../../../App";
import {
  buildDetails,
  buildSummary,
  diffFields,
  type FieldSpec,
} from "@lift-admin/utils/logChangeFormatter";
import jwtEncode from "jwt-encode";
import TableauViz from "./tableau-viz";
import { CodeIcon, LinkIcon } from "lucide-react";
import ReportDialogs from "./report-dialogs";

const REPORT_FIELD_SPECS: FieldSpec[] = [
  { key: "display_name", label: "Display Name" },
  { key: "embeded_url", label: "Embedded URL" },
  { key: "url_type", label: "URL Type" },
  { key: "description", label: "Description" },
  { key: "maintainer", label: "Maintainer" },
  { key: "contact_info", label: "Contact Info" },
  { key: "route_endpoint", label: "Route" },
];

const theme = createTheme({
  palette: {
    primary: { main: "#16306D" },
    secondary: { main: "rgba(0, 0, 0, 0)" },
  },
  typography: { fontFamily: '"Figtree", sans-serif' },
});

interface ItemsProps {
  toggle: string;
  searchText: string;
  openDialog: boolean;
  setOpenDialog: any;
  channels: any[];
  modules: any[];
}

interface TabData {
  name: string;
  id: string;
  url: string;
  isActive: boolean;
}

const Reports = ({ toggle, searchText, openDialog, setOpenDialog, channels, modules }: ItemsProps) => {
  const [loadData, setLoadData] = useState<boolean>(false);
  const [drawerItem, setDrawerItems] = useState<any>([]);
  const [drawerItemBackUp, setDrawerItemsBackup] = useState<any>([]);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [openTabsDialog, setOpenTabsDialog] = useState<boolean>(false);
  const [openSeparateReportsDialog, setOpenSeparateReportsDialog] = useState<boolean>(false);
  const [detectedTabs, setDetectedTabs] = useState<TabData[]>([]);
  const [groupName, setGroupName] = useState<string>("");
  const [currentIdData, setCurrentIdData] = useState<any>(null);
  const [display_name, setDisplayName] = useState<string | null>(null);
  const [maintainer, setMaintainer] = useState<string | null>(null);
  const [contact_info, setContactInfo] = useState<string | null>(null);
  const [url_type, setURLType] = useState<string | null>(null);
  const [embeded_url, setEmbededURL] = useState<string | null>(null);
  const [description, setDescription] = useState<string | null>(null);
  const [searchedResults, setSearchedResults] = useState<any>([]);
  const [checkTableauUrl, setCheckTableauUrl] = useState<string | null>(null);
  const [checkTableauToken, setCheckTableauToken] = useState<string | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<any>(null);
  const [selectedModule, setSelectedModule] = useState<any>(null);
  const [loadingAdd, setLoadingAdd] = useState<boolean>(false);
  const [loadingEdit, setLoadingEdit] = useState<boolean>(false);
  const [loadingDelete, setLoadingDelete] = useState<boolean>(false);
  // Tableau authentication credentials
  const connectedAppSecretId = "a55c4fbd-bdda-459d-8752-b1d5809224d6";
  const connectedAppSecretKey = "G1vY2QLbpnEE2FnwFI12rfBa2gYE3btdstoKYRP/jgY=";
  const connectedAppClientId = "2b62f4fd-2488-443d-abb4-d2287155f1bf";
  const siteName = "newengenlift";
  const currentUser = { email: "jteron@newengen.com" };

  // Function to create endpoint path
  const createEndPoint = (state: any) => {
    let endPointPath = "";

    if (state.selectedChannel?.name) {
      const channelPath = state.selectedChannel.name
        .toLowerCase()
        .replace(/\s+/g, "-");
      endPointPath += `/${channelPath}`;
    }

    if (state.selectedModule?.name) {
      const modulePath = state.selectedModule.name
        .toLowerCase()
        .replace(/\s+/g, "-");
      endPointPath += `/${modulePath}`;
    }

    endPointPath += `/`;

    return endPointPath;
  };

  // Function to get dynamic route_endpoint for a tab
  const getRouteEndpoint = (tab: TabData) => {
    const tabDisplayName = groupName ? `${groupName} - ${tab.name}` : tab.name;
    return createEndPoint({ selectedChannel, selectedModule }) + tabDisplayName.toLowerCase().replace(/\s+/g, "-");
  };

  const generateJWT = useCallback(() => {
    if (!currentUser?.email) {
      console.error("Invalid user email");
      return null;
    }
    try {
      const payload = {
        iss: connectedAppClientId,
        exp: Math.floor(Date.now() / 1000) + 5 * 60,
        jti: crypto.randomUUID(),
        aud: "tableau",
        sub: currentUser.email,
        scp: ["tableau:views:embed"],
      };
      return jwtEncode(payload, connectedAppSecretKey, {
        alg: "HS256",
        typ: "JWT",
        kid: connectedAppSecretId,
        iss: connectedAppClientId,
      });
    } catch (error) {
      console.error("JWT Generation Error:", error);
      return null;
    }
  }, [currentUser, connectedAppSecretId, connectedAppSecretKey, connectedAppClientId]);

  const authenticateWithTableau = useCallback(
    async (jwt: string) => {
      try {
        const response = await fetch(
          "https://us-east-1.online.tableau.com/vizportal/api/web/v1/auth/embed/signin",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "*/*",
              Origin: window.location.origin,
            },
            credentials: "include",
            body: JSON.stringify({ siteName, jwt }),
          }
        );
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`Authentication failed: ${JSON.stringify(errorData)}`);
        }
        const data = await response.json();
        console.log("Tableau authentication successful:", data);
        return data;
      } catch (error: any) {
        console.error("Authentication Error:", error);
        if (error.message.includes("401")) {
          throw new Error("Unauthorized | Please check Tableau credentials and user permissions");
        }
        throw error;
      }
    },
    [siteName]
  );

  const checkTableauReport = async (url: string): Promise<{ isTableau: boolean; tabs: TabData[] }> => {
    if (!url.toLowerCase().includes("tableau.com")) {
      console.log("Not a Tableau URL:", url);
      return { isTableau: false, tabs: [] };
    }
    try {
      const urlPattern = /^https:\/\/[a-z0-9-]+\.online\.tableau\.com\/t\/[a-zA-Z0-9_-]+\/views\/[a-zA-Z0-9_-]+\/[a-zA-Z0-9_-]+(?:\/[a-zA-Z0-9_-]+)*$/;
      if (!urlPattern.test(url)) {
        console.log("Invalid Tableau URL format:", url);
        throw new Error("Invalid Tableau URL format.");
      }
      const jwt = generateJWT();
      if (!jwt) {
        throw new Error("Failed to generate JWT token.");
      }
      await authenticateWithTableau(jwt);
      setCheckTableauUrl(url);
      setCheckTableauToken(jwt);

      const tabs = await new Promise<TabData[]>((resolve, reject) => {
        const timeout = setTimeout(() => {
          console.error("Timeout waiting for Tableau tabs");
          reject(new Error("Timeout waiting for Tableau viz to load"));
        }, 30000);
        const handleTabsLoaded = (tabs: TabData[]) => {
          console.log("Tabs loaded successfully:", tabs);
          clearTimeout(timeout);
          resolve(tabs);
        };
        const handleError = (error: string) => {
          clearTimeout(timeout);
          console.error("Error from TableauViz:", error);
          reject(new Error(error));
        };
        window.__tableauVizHandlers = { handleTabsLoaded, handleError };
      });
      return { isTableau: true, tabs };
    } catch (error: any) {
      console.error("Error in checkTableauReport:", error);
      Toastify("error", "Failed to check Tableau report tabs");
      return { isTableau: true, tabs: [] };
    } finally {
      setCheckTableauUrl(null);
      setCheckTableauToken(null);
    }
  };

  const getReports = async () => {
    setLoadData(true);
    try {
      const res = await apiClient.get("/drawer/v1/admin/drawer/structure/drawer-items-reports");
      const drawerReport = res?.data?.response?.drawerReport.sort(
        (a: { created_at: string }, b: { created_at: string }) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setDrawerItems(searchText.trim().length === 0 ? drawerReport : drawerReport.filter((item: any) => searchedResults.includes(item.id)));
      setDrawerItemsBackup(drawerReport);
    } catch {
      Toastify("error", "Something went wrong");
    } finally {
      setLoadData(false);
    }
  };

  useEffect(() => { getReports(); }, []);

  const resetState = () => {
    setDisplayName(null);
    setURLType(null);
    setEmbededURL(null);
    setDescription(null);
    setCurrentIdData(null);
    setMaintainer(null);
    setContactInfo(null);
    setDetectedTabs([]);
    setGroupName("");
    setCheckTableauUrl(null);
    setCheckTableauToken(null);
    setSelectedChannel(null);
    setSelectedModule(null);
  };

  const handleAddChannel = async (reports: Array<{
    display_name: string;
    embeded_url: string;
    url_type: string;
    description: string;
    maintainer: string;
    contact_info: string;
    route_endpoint?: string;
  }>) => {
    setLoadingAdd(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      for (const report of reports) {
        if (!report.display_name || !report.url_type || !report.embeded_url || !report.description) {
          Toastify("error", "Please fill all required fields");
          return;
        }
        const payload = {
          display_name: report.display_name,
          embeded_url: report.embeded_url,
          url_type: report.url_type,
          description: report.description,
          owner: user.id,
          modified_by: user.id,
          maintainer: report.maintainer,
          contact_info: report.contact_info,
          route_endpoint: report.route_endpoint,
        };
        const res = await apiClient.post("/drawer/v1/admin/drawer/structure/drawer-item-report", payload);
        const normalizedReport = {
          display_name: payload.display_name,
          embeded_url: payload.embeded_url,
          url_type: payload.url_type,
          description: payload.description,
          maintainer: payload.maintainer,
          contact_info: payload.contact_info,
          route_endpoint: payload.route_endpoint,
        };
        const desc = {
          message: buildSummary("Report", payload?.display_name, "created"),
          status: res.status,
          method: res.config.method,
          url: res.config.url,
          category: "drawer",
          action: "create_report",
          reportName: payload?.display_name,
          user_id: user?.id,
          user_name: user?.name,
          details: buildDetails(normalizedReport, REPORT_FIELD_SPECS, {
            skipEmpty: true,
          }),
        };
        await actionLog?.actionLogTrack(JSON.stringify(desc), false);
      }
      getReports();
      resetState();
      Toastify("success", "Report(s) added successfully");
    } catch (err: any) {
      Toastify("error", err.response?.data?.message || "Failed to add report");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const errorDesc = {
        message: `Failed to create Report by User (User ID: ${user?.id}, User Name: "${user?.name}")`,
        status: err.response?.status,
        method: "POST",
        url: "/drawer/v1/admin/drawer/structure/drawer-item-report",
        category: "drawer",
        action: "create_report",
        user_id: user?.id,
        user_name: user?.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    } finally {
      setLoadingAdd(false);
    }
  };

  const handleAddTableauTabs = async () => {
    setLoadingAdd(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const ownerId = user.id;
      const endpoint = createEndPoint({ selectedChannel, selectedModule }) + groupName.toLowerCase().replace(/\s+/g, "-");
      const grp = {
        name: groupName,
        path: endpoint,
        icon: "default-icon",
      };
      const tabs = detectedTabs.map((tab) => {
        const tabDisplayName = tab.name;
        const tabUrl = tab.url || `${embeded_url!.replace(/\/[^/]+$/, '')}/${tab.name}`;
        return {
          name: tabDisplayName,
          description: description!,
          url: tabUrl,
          url_type: url_type!,
          owner_id: ownerId,
          modified_by_id: ownerId,
          channel_id: selectedChannel?.id,
          contact_info: contact_info || "",
          maintainer: maintainer || "",
          module_id: selectedModule?.id,
          route_endpoint: createEndPoint({ selectedChannel, selectedModule }) + groupName.toLowerCase().replace(/\s+/g, "-") + "/" + tab.name.toLowerCase().replace(/\s+/g, "-"),
        };
      });
      const payload = { grp, tabs };
      const res = await apiClient.post("/drawer/v1/admin/drawer/structure/drawer-report-tableau-tabs", payload);
      Toastify("success", "Reports added successfully");
      const desc = {
        message: buildSummary("Report group", groupName, "created"),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "create_report_tableau_tabs",
        groupName,
        user_id: user?.id,
        user_name: user?.name,
        details: [
          { label: "Group Name", value: groupName || "Not Set" },
          {
            label: "Channel",
            value: selectedChannel?.name || "Not Set",
          },
          {
            label: "Module",
            value: selectedModule?.name || "Not Set",
          },
          {
            label: "Tabs",
            value:
              detectedTabs.map((tab) => tab.name).filter(Boolean).join(", ") ||
              "Not Set",
          },
          { label: "URL Type", value: url_type || "Not Set" },
          ...(description
            ? [{ label: "Description", value: description }]
            : []),
        ],
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
      getReports();
      resetState();
    } catch (err: any) {
      Toastify("error", err.response?.data?.message || "Failed to add reports");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const errorDesc = {
        message: `Failed to create Report group "${groupName}" by User (User ID: ${user?.id}, User Name: "${user?.name}")`,
        status: err.response?.status,
        method: "POST",
        url: "/drawer/v1/admin/drawer/structure/drawer-report-tableau-tabs",
        category: "drawer",
        action: "create_report_tableau_tabs",
        groupName,
        user_id: user?.id,
        user_name: user?.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    } finally {
      setLoadingAdd(false);
    }
  };

  const handleSubmit = async () => {
    if (!display_name || !embeded_url || !url_type || !description) {
      Toastify("error", "Please fill all required fields");
      return;
    }
    setLoadingAdd(true);
    try {
      const { isTableau, tabs } = await checkTableauReport(embeded_url);
      if (isTableau && tabs.length > 1) {
        setDetectedTabs(tabs);
        setOpenTabsDialog(true);
      } else {
        const routeEndpoint = createEndPoint({ selectedChannel, selectedModule }) + display_name!.toLowerCase().replace(/\s+/g, "-");
        await handleAddChannel([{
          display_name: display_name!,
          embeded_url: embeded_url!,
          url_type: url_type!,
          description: description!,
          maintainer: maintainer || "",
          contact_info: contact_info || "",
          route_endpoint: routeEndpoint,
        }]);
        setOpenDialog((prev: any) => ({ ...prev, report: false }));
      }
    } finally {
      setLoadingAdd(false);
    }
  };

  const handleTabsDialogChoice = async (separate: boolean) => {
    if (!display_name || !embeded_url || !url_type || !description) return;
    setLoadingAdd(true);
    try {
      if (separate) {
        await handleAddTableauTabs();
      } else {
        const routeEndpoint = createEndPoint({ selectedChannel, selectedModule }) + display_name!.toLowerCase().replace(/\s+/g, "-");
        await handleAddChannel([{
          display_name: display_name!,
          embeded_url: embeded_url!,
          url_type: url_type!,
          description: description!,
          maintainer: maintainer || "",
          contact_info: contact_info || "",
          route_endpoint: routeEndpoint,
        }]);
      }
      setOpenSeparateReportsDialog(false);
      setOpenTabsDialog(false);
      setOpenDialog((prev: any) => ({ ...prev, report: false }));
    } finally {
      setLoadingAdd(false);
    }
  };

  const handleDelete = async (data: any) => {
    setLoadingDelete(true);
    try {
      if (!data?.id) {
        Toastify("error", "Invalid Report ID");
        return;
      }
      const res = await apiClient.delete(`/drawer/v1/admin/drawer/structure/drawer-report/${data?.id}`);
      getReports();
      setOpenDeleteDialog(false);
      Toastify("success", "Item Deleted successfully");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const normalizedReport = {
        display_name: data?.display_name,
        embeded_url: data?.embeded_url,
        url_type: data?.url_type,
        description: data?.description,
        maintainer: data?.maintainer,
        contact_info: data?.contact_info,
        route_endpoint: data?.route_endpoint,
      };
      const desc = {
        message: buildSummary("Report", data?.display_name, "deleted"),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "delete_report",
        reportId: data?.id,
        reportName: data?.display_name,
        user_id: user?.id,
        user_name: user?.name,
        details: buildDetails(normalizedReport, REPORT_FIELD_SPECS, {
          skipEmpty: true,
        }),
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
    } catch (err: any) {
      Toastify("error", "Report not deleted");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const errorDesc = {
        message: `Failed to delete Report (Report ID: ${data?.id}, Report Name: "${data?.display_name}") by User (User ID: ${user?.id}, User Name: "${user?.name}")`,
        status: err.response?.status,
        method: "DELETE",
        url: `/drawer/v1/admin/drawer/structure/drawer-report/${data?.id}`,
        category: "drawer",
        action: "delete_report",
        reportId: data?.id,
        reportName: data?.display_name,
        user_id: user?.id,
        user_name: user?.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleEdit = async () => {
    if (!currentIdData?.id || !display_name || !url_type || !embeded_url || !description) {
      Toastify("error", "Please fill all required fields");
      return;
    }
    setLoadingEdit(true);
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    try {
      const routeEndpoint = createEndPoint({ selectedChannel, selectedModule }) + display_name!.toLowerCase().replace(/\s+/g, "-");
      const payload = {
        display_name,
        embeded_url,
        url_type,
        description,
        owner: user.id,
        modified_by: user.id,
        maintainer,
        contact_info,
        route_endpoint: routeEndpoint,
      };
      const res = await apiClient.put(`/drawer/v1/admin/drawer/structure/drawer-report/${currentIdData?.id}`, payload);
      Toastify("success", "Item updated successfully");
      const previousReport = {
        display_name: currentIdData?.display_name,
        embeded_url: currentIdData?.embeded_url,
        url_type: currentIdData?.url_type,
        description: currentIdData?.description,
        maintainer: currentIdData?.maintainer || "",
        contact_info: currentIdData?.contact_info || "",
      };
      const nextReport = {
        display_name: payload.display_name,
        embeded_url: payload.embeded_url,
        url_type: payload.url_type,
        description: payload.description,
        maintainer: payload.maintainer || "",
        contact_info: payload.contact_info || "",
      };
      getReports();
      handleEditDialogClose();
      const updateFieldSpecs = REPORT_FIELD_SPECS.filter(
        (spec) => spec.key !== "route_endpoint"
      );
      const changes = diffFields(previousReport, nextReport, updateFieldSpecs);
      const desc = {
        message: buildSummary(
          "Report",
          payload.display_name || currentIdData?.display_name,
          "updated",
          changes.length
        ),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "update_report",
        reportId: currentIdData?.id,
        reportName: payload.display_name || currentIdData?.display_name,
        user_id: user?.id,
        user_name: user?.name,
        changes,
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
    } catch (err: any) {
      Toastify("error", err.response?.data?.message || "Failed to update report");
      const errorDesc = {
        message: `Failed to update Report (Report ID: ${currentIdData?.id}, Report Name: "${currentIdData?.display_name}") by User (User ID: ${user?.id}, User Name: "${user?.name}")`,
        status: err.response?.status,
        method: "PUT",
        url: `/drawer/v1/admin/drawer/structure/drawer-report/${currentIdData?.id}`,
        category: "drawer",
        action: "update_report",
        reportId: currentIdData?.id,
        reportName: currentIdData?.display_name,
        user_id: user?.id,
        user_name: user?.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog((prev: any) => ({ ...prev, report: false }));
    resetState();
  };

  const handleEditOpen = (data: any) => {
    setCurrentIdData(data);
    setDisplayName(data?.display_name);
    setEmbededURL(data?.embeded_url);
    setDescription(data?.description);
    setURLType(data?.url_type);
    setMaintainer(data?.maintainer);
    setContactInfo(data?.contact_info);
    setOpenEditDialog(true);
  };

  const handleEditDialogClose = () => {
    setDisplayName(null);
    setEmbededURL(null);
    setDescription(null);
    setURLType(null);
    setCurrentIdData(null);
    setOpenEditDialog(false);
    setMaintainer(null);
    setContactInfo(null);
  };

  const allUrlTypes = ["Normal", "Fiscal", "FinTech"];

  const searchResults = (query: any) => {
    const filteredUsers = [...drawerItemBackUp];
    let results = [];
    for (let i = 0; i < filteredUsers.length; i++) {
      if (
        filteredUsers[i]["display_name"]?.toLowerCase().includes(query.toLowerCase()) ||
        filteredUsers[i]["embeded_url"]?.toLowerCase().includes(query.toLowerCase()) ||
        filteredUsers[i]["url_type"]?.toLowerCase().includes(query.toLowerCase())
      )
        results.push(filteredUsers[i]);
    }
    setSearchedResults(results.map((item: any) => item.id));
    setDrawerItems(results);
  };

  useEffect(() => { searchResults(searchText); }, [searchText]);

  const authenticateTableauOnce = async () => {
    const jwt = generateJWT();
    if (!jwt) {
      throw new Error("Failed to generate JWT");
    }
    await authenticateWithTableau(jwt);
    return jwt;
  };

  const checkAndAddMissingTableauTabs = async () => {
    setLoadData(true);
    console.info(`[${new Date().toISOString()}] Starting checkAndAddMissingTableauTabs function`);

    try {
      // Fetch existing reports
      console.info(`[${new Date().toISOString()}] Fetching existing reports from API`);
      const res = await apiClient.get("/drawer/v1/admin/drawer/structure/drawer-items-reports");
      const existingReports = res?.data?.response?.drawerReport || [];
      console.info(`[${new Date().toISOString()}] Fetched ${existingReports.length} reports`);

      const tableauReports = existingReports.filter((report: any) =>
        report.embeded_url && report.embeded_url.toLowerCase().includes("tableau.com") && report.auto_tabs
      );
      console.info(`[${new Date().toISOString()}] Found ${tableauReports.length} Tableau reports to check`);

      // Authenticate once before processing reports
      console.info(`[${new Date().toISOString()}] Authenticating with Tableau...`);
      const jwt = await authenticateTableauOnce();
      console.info(`[${new Date().toISOString()}] Tableau authentication successful`);

      const missingTabs: any[] = [];

      // Process each Tableau report
      for (let i = 0; i < tableauReports.length; i++) {
        const report = tableauReports[i];
        console.info(`[${new Date().toISOString()}] [${i + 1}/${tableauReports.length}] Checking report: ${report.display_name} - URL: ${report.embeded_url}`);

        try {
          setCheckTableauUrl(report.embeded_url);
          setCheckTableauToken(jwt); // Reuse the single JWT

          console.info(`[${new Date().toISOString()}] Waiting for tabs to load for report: ${report.display_name}`);
          const tabs = await new Promise<TabData[]>((resolve, reject) => {
            const timeout = setTimeout(() => {
              console.error(`[${new Date().toISOString()}] Timeout waiting for Tableau tabs for URL: ${report.embeded_url}`);
              reject(new Error(`Timeout waiting for Tableau viz to load for ${report.display_name}`));
            }, 30000); // 30-second timeout

            window.__tableauVizHandlers = {
              handleTabsLoaded: (tabs) => {
                clearTimeout(timeout);
                console.info(`[${new Date().toISOString()}] Loaded ${tabs.length} tabs for report ${report.display_name}`);
                resolve(tabs);
              },
              handleError: (error) => {
                clearTimeout(timeout);
                console.error(`[${new Date().toISOString()}] Error from TableauViz for ${report.display_name}:`, error);
                reject(new Error(error));
              }
            };
          });

          if (tabs.length > 0) {
            console.info(`[${new Date().toISOString()}] Found ${tabs.length} tabs for report ${report.display_name}. Checking for missing tabs...`);

            for (const tab of tabs) {
              const tabUrl = tab.url || `${report.embeded_url.replace(/\/[^/]+$/, '')}/${tab.name}`;
              const tabEndpoint = report.route_endpoint + "/" + tab.name.toLowerCase().replace(/\s+/g, "-");

              const tabExists = existingReports.some((existingReport: any) =>
                existingReport.embeded_url === tabUrl ||
                existingReport.route_endpoint === tabEndpoint
              );

              if (!tabExists) {
                if (missingTabs.length > 0) {
                  const existingTab = missingTabs.find((mt: any) => mt.tab.url === tabUrl);
                  if (existingTab) {
                    console.info(`[${new Date().toISOString()}] Tab ${tab.name} already in missing tabs list, skipping`);
                    continue;
                  }
                }

                console.info(`[${new Date().toISOString()}] Found missing tab: ${tab.name} for report ${report.display_name}`);
                missingTabs.push({
                  parentReport: report,
                  tab: { name: tab.name, url: tabUrl }
                });
              } else {
                console.info(`[${new Date().toISOString()}] Tab ${tab.name} already exists, skipping`);
              }
            }
          } else {
            console.info(`[${new Date().toISOString()}] No tabs found for report ${report.display_name}`);
          }
        } catch (error) {
          console.error(`[${new Date().toISOString()}] Failed for report ${report.display_name} (URL: ${report.embeded_url}):`, error);
          continue;
        } finally {
          setCheckTableauUrl(null);
          setCheckTableauToken(null);
        }
      }

      console.info(`[${new Date().toISOString()}] Found ${missingTabs.length} missing tabs that need to be added`);

      if (missingTabs.length > 0) {
        // Process each missing tab one at a time
        console.info(`[${new Date().toISOString()}] Starting to add ${missingTabs.length} missing tabs`);

        const addTabPromises = missingTabs.map((mt: any, index: number) => {
          return new Promise((resolve, reject) => {
            console.info(`[${new Date().toISOString()}] [${index + 1}/${missingTabs.length}] Adding tab ${mt.tab.name} for report ${mt.parentReport.display_name}`);

            apiClient.post("/drawer/v1/admin/drawer/structure/drawer-items-reports-tabs-auto", {
              parentId: mt.parentReport.id,
              tab: {
                name: mt.tab.name,
                url: mt.tab.url
              }
            })
              .then(res => {
                console.info(`[${new Date().toISOString()}] Successfully added tab ${mt.tab.name} for report ${mt.parentReport.display_name}`);
                resolve(res);
              })
              .catch(error => {
                console.error(`[${new Date().toISOString()}] Failed to add tab ${mt.tab.name} for report ${mt.parentReport.display_name}:`, error);
                reject(error);
              });
          });
        });

        // Wait for all tabs to be processed
        console.info(`[${new Date().toISOString()}] Waiting for all tab additions to complete...`);
        const results = await Promise.allSettled(addTabPromises);

        const successCount = results.filter(r => r.status === 'fulfilled').length;
        const failureCount = results.filter(r => r.status === 'rejected').length;

        console.info(`[${new Date().toISOString()}] All tab additions completed. Success: ${successCount}, Failed: ${failureCount}`);

        if (successCount > 0) {
          Toastify("success", `Successfully added ${successCount} missing tabs`);
        }

        if (failureCount > 0) {
          console.error(`[${new Date().toISOString()}] ${failureCount} tabs failed to add`);
        }
      } else {
        console.info(`[${new Date().toISOString()}] No missing tabs to add`);
        Toastify("info", "No missing tabs found to add");
      }

      return missingTabs.length > 0 ? true : true; // Return true even if no tabs were found, as the process completed successfully
    } catch (error) {
      console.error(`[${new Date().toISOString()}] Error checking Tableau reports:`, error);
      Toastify("error", `Failed to check Tableau reports: ${error || 'Unknown error'}`);
      return false;
    } finally {
      console.info(`[${new Date().toISOString()}] Completed checkAndAddMissingTableauTabs function`);
      setLoadData(false);
    }
  };


  return (
    <>
      <ThemeProvider theme={theme}>

        <Paper className="table-paper" style={{ paddingLeft: 0 }}>
          <div className="ag-theme-alpine" style={{ height: "100%", width: "100%" }}>
            <AgGridReact
              columnDefs={useMemo(() => [
                { field: "display_name", headerName: "Display Name", width: 250, sortable: true, resizable: true, editable: true, flex: 1, cellStyle: { display: "flex", alignItems: "center" } },
                { field: "embeded_url", headerName: "Embedded URL", width: 250, sortable: true, resizable: true, editable: true, flex: 1, cellStyle: { display: "flex", alignItems: "center" } },
                { field: "url_type", headerName: "URL Type", width: 250, sortable: true, resizable: true, editable: true, flex: 1, cellStyle: { display: "flex", alignItems: "center" } },
                { field: "description", headerName: "Description", width: 250, sortable: true, resizable: true, editable: true, flex: 1, cellStyle: { display: "flex", alignItems: "center" } },
                { field: "owner", headerName: "Owner", width: 250, sortable: true, resizable: true, editable: true, flex: 1, cellStyle: { display: "flex", alignItems: "center" }, valueGetter: (params: any) => params?.data?.owner?.name || "" },
                { field: "maintainer", headerName: "Maintainer", width: 250, sortable: true, resizable: true, editable: true, flex: 1, cellStyle: { display: "flex", alignItems: "center" }, valueGetter: (params: any) => params?.data?.maintainer || "" },
                { field: "contact_info", headerName: "Contact Info", width: 250, sortable: true, resizable: true, editable: true, flex: 1, cellStyle: { display: "flex", alignItems: "center" }, valueGetter: (params: any) => params?.data?.contact_info || "" },
                { field: "modified_by", headerName: "Modified By", width: 250, sortable: true, resizable: true, editable: true, flex: 1, cellStyle: { display: "flex", alignItems: "center" }, valueGetter: (params: any) => params?.data?.modified_by?.name || "" },
                { field: "created_at", headerName: "Created At", width: 200, sortable: true, resizable: true, editable: false, flex: 1, cellStyle: { display: "flex", alignItems: "center" }, valueFormatter: (params: { value: string }) => params.value ? new Date(params.value).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: true }) : "" },
                { field: "updated_at", headerName: "Updated At", width: 200, sortable: true, resizable: true, editable: false, flex: 1, cellStyle: { display: "flex", alignItems: "center" }, valueFormatter: (params: { value: string }) => params.value ? new Date(params.value).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: true }) : "" },
                {
                  field: "actions",
                  headerName: "Actions",
                  width: 150,
                  sortable: false,
                  resizable: false,
                  editable: false,
                  pinned: "right" as const,
                  headerClass: "center-header",
                  cellStyle: { display: "flex", alignItems: "center" },
                  cellRenderer: (params: { data: any }) => (
                    <div style={{ display: "flex", gap: "5px" }}>
                      <Button onClick={() => handleEditOpen(params?.data)} style={{ background: "transparent", border: "none", cursor: "pointer" }}>
                        <Edit style={{ color: "black" }} />
                      </Button>
                      <Button onClick={() => { setOpenDeleteDialog(true); setCurrentIdData(params?.data); }} style={{ background: "transparent", border: "none", cursor: "pointer" }}>
                        <Delete style={{ color: "red" }} />
                      </Button>
                    </div>
                  ),
                },
              ], [])}
              rowData={drawerItem}
              defaultColDef={{ resizable: true, sortable: true, editable: false }}
              rowHeight={80}
              onCellValueChanged={(params: { data: any }) => console.log("Data after change:", params.data)}
              pagination={true}
              paginationPageSize={20}
              suppressPaginationPanel={false}
            />
          </div>
        </Paper>
        {checkTableauUrl && checkTableauToken && (
          <TableauViz
            url={checkTableauUrl}
            token={checkTableauToken}
            onTabsLoaded={(tabs) => window.__tableauVizHandlers?.handleTabsLoaded(tabs as any)}
            onError={(error) => window.__tableauVizHandlers?.handleError(error)}
          />
        )}
        <button
          id="tableau-auto-script"
          onClick={async () => {
            try {
              // Add timestamp to the logs
              console.info(`[${new Date().toISOString()}] Starting Tableau tabs synchronization...`);

              // Better logging before function call
              console.info(`[${new Date().toISOString()}] Calling checkAndAddMissingTableauTabs function`);
              const res = await checkAndAddMissingTableauTabs();

              // Add detailed result logging
              console.info(`[${new Date().toISOString()}] Tableau synchronization completed with result: ${res}`);

              // Set data attributes with more information
              const btn = document.getElementById('tableau-auto-script');
              if (btn) {
                if (res === true) {
                  // Add timestamp to data attributes
                  btn.setAttribute('data-result', 'SUCCESS');
                  btn.setAttribute('data-timestamp', new Date().toISOString());
                  btn.setAttribute('data-message', 'Tableau tabs were successfully synchronized');
                  console.info(`[${new Date().toISOString()}] DATA_RESULT: SUCCESS - Tableau tabs were successfully synchronized`);
                } else {
                  btn.setAttribute('data-result', 'UNSUCCESSFUL');
                  btn.setAttribute('data-timestamp', new Date().toISOString());
                  btn.setAttribute('data-message', 'Failed to synchronize Tableau tabs');
                  console.info(`[${new Date().toISOString()}] DATA_RESULT: UNSUCCESSFUL - Failed to synchronize Tableau tabs`);
                }
              }
            } catch (error) {
              // Log any errors that occur
              console.error(`[${new Date().toISOString()}] ERROR during Tableau synchronization:`, error);

              // Set error data attributes
              const btn = document.getElementById('tableau-auto-script');
              if (btn) {
                btn.setAttribute('data-result', 'ERROR');
                btn.setAttribute('data-timestamp', new Date().toISOString());
                btn.setAttribute('data-message', `Error: ${error || 'Unknown error occurred'}`);
                console.info(`[${new Date().toISOString()}] DATA_RESULT: ERROR - ${error || 'Unknown error occurred'}`);
              }
            }
          }}
          style={{ color: '#f9f9f9' }}
        >Check Tableau Tabs</button>
        <ReportDialogs
          openDialog={openDialog}
          openEditDialog={openEditDialog}
          openDeleteDialog={openDeleteDialog}
          display_name={display_name || ''}
          embeded_url={embeded_url || ''}
          url_type={url_type || ''}
          description={description || ''}
          maintainer={maintainer || ''}
          contact_info={contact_info || ''}
          allUrlTypes={allUrlTypes}
          currentIdData={currentIdData}
          setDisplayName={setDisplayName}
          setEmbededURL={setEmbededURL}
          setURLType={setURLType}
          setDescription={setDescription}
          setMaintainer={setMaintainer}
          setContactInfo={setContactInfo}
          setOpenDialog={setOpenDialog}
          setOpenEditDialog={setOpenEditDialog}
          setOpenDeleteDialog={setOpenDeleteDialog}
          handleSubmit={handleSubmit}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          handleDialogClose={handleDialogClose}
          handleEditDialogClose={handleEditDialogClose}
          loadingAdd={loadingAdd}
        // loadingEdit={loadingEdit}
        // loadingDelete={loadingDelete}
        />
        <Dialog open={openTabsDialog} onClose={() => setOpenTabsDialog(false)}>
          <DialogTitle>Tableau Report Tabs Detected</DialogTitle>
          <DialogContent>
            <Typography>
              This report contains multiple tabs: {detectedTabs.map((tab) => tab.name).join(", ")}. Do you want to save it as separate reports or as a single report?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => { setOpenTabsDialog(false); handleTabsDialogChoice(false); }}
              color="primary"
              disabled={loadingAdd}
              startIcon={loadingAdd ? <CircularProgress size={20} /> : null}
            >
              {loadingAdd ? "Saving..." : "Save as Single Report"}
            </Button>
            <Button
              onClick={() => { setOpenTabsDialog(false); setOpenSeparateReportsDialog(true); }}
              color="primary"
              disabled={loadingAdd}
            >
              Save as Separate Reports
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openSeparateReportsDialog}
          onClose={() => setOpenSeparateReportsDialog(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Save Separate Reports</DialogTitle>
          <DialogContent>
            {/* Section 1: Report Group Details */}
            <Typography variant="h6" gutterBottom>
              Drawer Location Details
            </Typography>
            <TextField
              label="Group Name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              fullWidth
              style={{ marginBottom: "16px" }}
              required
            />
            <Autocomplete
              options={channels}
              getOptionLabel={(option) => option.name}
              onChange={(event, value) => setSelectedChannel(value)}
              value={selectedChannel}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Channel Name"
                  fullWidth
                  style={{ marginBottom: "16px" }}
                  required
                />
              )}
            />
            <Autocomplete
              options={modules}
              getOptionLabel={(option) => option.name}
              onChange={(event, value) => setSelectedModule(value)}
              value={selectedModule}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Module Name"
                  fullWidth
                  style={{ marginBottom: "16px" }}
                  required
                />
              )}
            />

            {/* Divider between sections */}
            <Divider style={{ margin: "16px 0" }} />

            {/* Section 2: Tabs */}
            <Typography variant="h6" gutterBottom>
              Tabs
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: "16px" }}>
              Please provide a name for each detected tab below.
            </Typography>
            {detectedTabs.map((tab, index) => (
              <Box key={index} sx={{ marginBottom: "16px" }}>
                {/* URL with Icon */}
                <Grid container alignItems="center" spacing={1}>
                  <Grid item>
                    <LinkIcon />
                  </Grid>
                  <Grid item xs>
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}
                    >
                      URL: {tab.url}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Endpoint with Icon */}
                <Grid container alignItems="center" spacing={1}>
                  <Grid item>
                    <CodeIcon />
                  </Grid>
                  <Grid item xs>
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}
                    >
                      Endpoint: {getRouteEndpoint(tab)}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Tab Name Input */}
                <TextField
                  label={`Tab ${index + 1} Name`}
                  value={tab.name}
                  onChange={(e) => {
                    const updatedTabs = [...detectedTabs];
                    updatedTabs[index].name = e.target.value;
                    setDetectedTabs(updatedTabs);
                  }}
                  fullWidth
                  sx={{ marginTop: "8px" }}
                />
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpenSeparateReportsDialog(false)}
              color="primary"
              disabled={loadingAdd}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!groupName || !selectedChannel || !selectedModule) {
                  Toastify("error", "Please fill all required fields");
                  return;
                }
                handleTabsDialogChoice(true);
                setOpenSeparateReportsDialog(false);
              }}
              color="primary"
              disabled={loadingAdd || !groupName || !selectedChannel || !selectedModule}
              startIcon={loadingAdd ? <CircularProgress size={20} /> : null}
            >
              {loadingAdd ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </>
  );
};

declare global {
  interface Window {
    __tableauVizHandlers?: {
      handleTabsLoaded: (tabs: TabData[]) => void;
      handleError: (error: string) => void;
    };
  }
}

export default Reports;